import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../lib/analytics';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import PhoneInput from '../components/PhoneInput';
import { Briefcase, MapPin, Clock, Users, GraduationCap, Heart, TrendingUp, ArrowRight, Upload, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const galleryImages = [
  {
    src: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_0',
  },
  {
    src: 'https://images.unsplash.com/photo-1604328698692-f76ea9498e76?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_1',
  },
  {
    src: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_2',
  },
  {
    src: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_3',
  },
  {
    src: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_4',
  },
  {
    src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=70',
    alt: 'jobs.gallery.alt_5',
  },
];

const openPositions = [
  {
    id: 1,
    title: 'jobs.positions.0.title',
    type: 'jobs.positions.0.type',
    location: 'jobs.positions.0.location',
    experience: 'jobs.positions.0.experience',
    description: 'jobs.positions.0.description',
    requirements: [
      'jobs.positions.0.requirements.0',
      'jobs.positions.0.requirements.1',
      'jobs.positions.0.requirements.2',
      'jobs.positions.0.requirements.3',
    ],
  },
  {
    id: 2,
    title: 'jobs.positions.1.title',
    type: 'jobs.positions.1.type',
    location: 'jobs.positions.1.location',
    experience: 'jobs.positions.1.experience',
    description: 'jobs.positions.1.description',
    requirements: [
      'jobs.positions.1.requirements.0',
      'jobs.positions.1.requirements.1',
      'jobs.positions.1.requirements.2',
      'jobs.positions.1.requirements.3',
    ],
  },
  {
    id: 3,
    title: 'jobs.positions.2.title',
    type: 'jobs.positions.2.type',
    location: 'jobs.positions.2.location',
    experience: 'jobs.positions.2.experience',
    description: 'jobs.positions.2.description',
    requirements: [
      'jobs.positions.2.requirements.0',
      'jobs.positions.2.requirements.1',
      'jobs.positions.2.requirements.2',
      'jobs.positions.2.requirements.3',
    ],
  },
  {
    id: 4,
    title: 'jobs.positions.3.title',
    type: 'jobs.positions.3.type',
    location: 'jobs.positions.3.location',
    experience: 'jobs.positions.3.experience',
    description: 'jobs.positions.3.description',
    requirements: [
      'jobs.positions.3.requirements.0',
      'jobs.positions.3.requirements.1',
      'jobs.positions.3.requirements.2',
      'jobs.positions.3.requirements.3',
    ],
  },
  {
    id: 5,
    title: 'jobs.positions.4.title',
    type: 'jobs.positions.4.type',
    location: 'jobs.positions.4.location',
    experience: 'jobs.positions.4.experience',
    description: 'jobs.positions.4.description',
    requirements: [
      'jobs.positions.4.requirements.0',
      'jobs.positions.4.requirements.1',
      'jobs.positions.4.requirements.2',
      'jobs.positions.4.requirements.3',
      'jobs.positions.4.requirements.4',
    ],
  },
];

const benefits = [
  { icon: TrendingUp, title: 'jobs.benefits.0.title', desc: 'jobs.benefits.0.desc' },
  { icon: Heart, title: 'jobs.benefits.1.title', desc: 'jobs.benefits.1.desc' },
  { icon: Users, title: 'jobs.benefits.2.title', desc: 'jobs.benefits.2.desc' },
  { icon: GraduationCap, title: 'jobs.benefits.3.title', desc: 'jobs.benefits.3.desc' },
];

const Jobs = () => {
  const { t } = useTranslation();
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', position: '' });
  const [cvFile, setCvFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { toast } = useToast();

  const handleApply = (job) => {
    setSelectedJob(job);
    setForm({ ...form, position: t(job.title) });
    setTimeout(() => {
      document.getElementById('bewerbung')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (cvFile && cvFile.size > 10 * 1024 * 1024) {
      setErrorMsg(t('jobs.form.error_file_size'));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone || '');
      fd.append('position', form.position || '');
      fd.append('message', form.message || '');
      if (cvFile) fd.append('cv', cvFile);
      const { data } = await axios.post(`${API}/contact/bewerbung`, fd);

      trackEvent('bewerbung_submitted', {
        bewerbung_id: data?.bewerbung_id,
        position: form.position || 'Initiativbewerbung',
        has_cv: !!cvFile,
      });
      toast({
        title: t('jobs.form.success_title'),
        description: t('jobs.form.success_description', { bewerbung_id: data.bewerbung_id, email: form.email }),
      });
      setForm({ name: '', email: '', phone: '', message: '', position: '' });
      setCvFile(null);
      setFileName('');
      setSelectedJob(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || t('jobs.form.error_generic');
      setErrorMsg(detail);
      toast({
        title: t('jobs.form.error_title'),
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={t('jobs.seo.title')}
        description={t('jobs.seo.description')}
        path="/karriere"
      />
      <Header />
      <main className="pt-[150px]">
        {/* Page hero */}
        <section className="bg-[#1B3A5C] text-white py-20 lg:py-28 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              {t('jobs.hero.label')}
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">{t('jobs.hero.title')}</h1>
            <p className="max-w-2xl text-white/80 text-lg leading-relaxed">
              {t('jobs.hero.description')}
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif font-light text-[#1B3A5C] text-3xl md:text-4xl mb-12 text-center">
              {t('jobs.benefits_section.heading')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b) => (
                <div
                  key={b.title}
                  className="group p-8 bg-[#f5f8fa] hover:bg-white border border-transparent hover:border-[#b8893d]/30 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-[#1B3A5C] group-hover:bg-[#b8893d] rounded-sm flex items-center justify-center mb-5 transition-colors">
                    <b.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-[#1B3A5C] font-semibold tracking-wide mb-2">{t(b.title)}</h3>
                  <p className="text-[#34536b] text-sm leading-relaxed">{t(b.desc)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#b8893d] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              {t('jobs.gallery_section.label')}
            </div>
            <h2 className="font-serif font-light text-[#1B3A5C] text-3xl md:text-4xl mb-10 max-w-2xl">
              {t('jobs.gallery_section.heading')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {galleryImages.map((img, i) => (
                <div
                  key={img.src}
                  data-testid={`karriere-gallery-${i}`}
                  className={`group relative overflow-hidden rounded-sm bg-[#1B3A5C]/5 ${
                    i === 0 ? 'md:col-span-2 md:row-span-2 aspect-square md:aspect-auto' : 'aspect-[4/3]'
                  }`}
                >
                  <img
                    src={img.src}
                    alt={t(img.alt)}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A5C]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                    <span className="text-white text-xs tracking-wider">{t(img.alt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open positions */}
        <section className="py-20 bg-[#eaf1f5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif font-light text-[#1B3A5C] text-3xl md:text-4xl mb-12">{t('jobs.positions_section.heading')}</h2>
            <div className="space-y-5">
              {openPositions.map((job) => (
                <div
                  key={job.id}
                  className="group bg-white p-6 lg:p-8 rounded-sm border-l-4 border-[#b8893d] hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-[#b8893d] text-xs tracking-[0.2em] mb-2">
                        <Briefcase className="w-3.5 h-3.5" />
                        {t('jobs.positions_section.label')}
                      </div>
                      <h3 className="text-[#1B3A5C] font-serif text-2xl mb-3">{t(job.title)}</h3>
                      <p className="text-[#34536b] text-sm leading-relaxed mb-4 max-w-3xl">{t(job.description)}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-[#34536b]">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-[#b8893d]" /> {t(job.type)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#b8893d]" /> {t(job.location)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-[#b8893d]" /> {t(job.experience)}
                        </span>
                      </div>
                      <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                        {job.requirements.map((r) => (
                          <li key={r} className="flex items-start gap-2 text-[#34536b] text-sm">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#b8893d] mt-1 flex-shrink-0" />
                            <span>{t(r)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button
                      onClick={() => handleApply(job)}
                      className="bg-[#1B3A5C] hover:bg-[#0f2740] text-white tracking-[0.15em] text-xs px-6 py-6 self-start lg:self-center"
                    >
                      {t('jobs.positions_section.apply_button')}
                      <ArrowRight className="ml-2 w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application form */}
        <section id="bewerbung" className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif font-light text-[#1B3A5C] text-3xl md:text-4xl mb-3">{t('jobs.form.heading')}</h2>
            <p className="text-[#34536b] mb-10">
              {selectedJob
                ? t('jobs.form.subheading_selected', { position: t(selectedJob.title) })
                : t('jobs.form.subheading_default')}
            </p>
            <form onSubmit={handleSubmit} className="bg-[#f5f8fa] p-8 lg:p-10 border-l-4 border-[#b8893d] space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_name')}</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_email')}</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_phone')}</label>
                  <PhoneInput
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testId="jobs-phone"
                    inputClassName="px-4 py-3 bg-white border-gray-200 focus:border-[#b8893d] focus:ring-2 focus:ring-[#b8893d]/20"
                  />
                </div>
                <div>
                  <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_position')}</label>
                  <input
                    value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_message')}</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2">{t('jobs.form.label_cv')}</label>
                <label className="flex items-center justify-center gap-3 w-full py-6 bg-white border-2 border-dashed border-gray-300 hover:border-[#b8893d] cursor-pointer transition-colors" data-testid="bewerbung-file-label">
                  <Upload className="w-5 h-5 text-[#b8893d]" />
                  <span className="text-sm text-[#34536b]">
                    {fileName || t('jobs.form.cv_placeholder')}
                  </span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    data-testid="bewerbung-cv-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setCvFile(f);
                      setFileName(f ? `${f.name} (${(f.size / 1024 / 1024).toFixed(2)} MB)` : '');
                    }}
                  />
                </label>
              </div>
              {errorMsg && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-sm" data-testid="bewerbung-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="bewerbung-submit"
                className="w-full md:w-auto bg-[#b8893d] hover:bg-[#a07730] text-white tracking-[0.2em] text-xs px-10 py-6"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 w-3.5 h-3.5 animate-spin" />
                    {t('jobs.form.submit_loading')}
                  </>
                ) : (
                  <>
                    {t('jobs.form.submit_button')}
                    <ArrowRight className="ml-2 w-3.5 h-3.5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Jobs;
